'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import MenuItem from './MenuItem';
import Image from 'next/image';
import { cn, shouldOpenInNewTab } from '@/app/lib';
import type { IMegaMenuNavigationItemColumn, AdvertisingBanner } from '../../Header/interfaces';
import { Typography } from '@workspace/ui/common';
import { getActivePathFromLink } from '@/app/lib';
import { MegaMenuContainer } from '../MegaMenuContainer';
import { sanityImageSrc } from '@/sanity/lib/image';
import { normalizeAltText } from '@workspace/constants';
import { getAdvertisingBannerAlt, getAdvertisingBannerImage } from '../utils/advertisingBanner';

interface ListMegaMenuProps {
  adBanner?: AdvertisingBanner;
  items?: IMegaMenuNavigationItemColumn[];
  isClosing?: boolean;
}

// Hook that follows your Tailwind md override (>= 992px is desktop)
function useIsDesktop(minWidth = 992) {
  const [isDesktop, setIsDesktop] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(`(min-width: ${minWidth}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(`(min-width: ${minWidth}px)`);
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
    } else {
      // Legacy Safari support (deprecated addListener)
      (mq as any).addListener?.(onChange as any);
    }

    setIsDesktop(mq.matches);

    return () => {
      if (typeof mq.removeEventListener === 'function') {
        mq.removeEventListener('change', onChange);
      } else {
        (mq as any).removeListener?.(onChange as any);
      }
    };
  }, [minWidth]);

  return isDesktop;
}

export default function ListMegaMenu({ adBanner, items = [], isClosing = false }: ListMegaMenuProps) {
  const isDesktop = useIsDesktop(992);
  const adBannerImage = getAdvertisingBannerImage(adBanner);
  const adBannerAlt = getAdvertisingBannerAlt(adBanner, 'ad banner');

  // Items that are actually rendered on the left side when an ad banner exists
  const visibleMenuItems = useMemo(() => items, [items]);

  // Helper to build a Set of expanded keys for all visible items
  const getAllExpandedSet = useCallback(() => new Set(visibleMenuItems.map((i) => i._key)), [visibleMenuItems]);

  // Initial state (mobile: collapsed; desktop: expanded)
  const [expandedItems, setExpandedItems] = useState<Set<string>>(() => (isDesktop ? getAllExpandedSet() : new Set()));

  // Keep expanded state in sync when switching between mobile/desktop or data changes
  useEffect(() => {
    if (isDesktop) {
      // Desktop: always expanded
      setExpandedItems(getAllExpandedSet());
    } else {
      // Mobile: keep user state where possible, but drop any keys that no longer exist
      setExpandedItems((prev) => {
        const next = new Set<string>();
        for (const k of prev) {
          if (visibleMenuItems.some((i) => i._key === k)) next.add(k);
        }
        return next;
      });
    }
  }, [isDesktop, items, adBanner, getAllExpandedSet, visibleMenuItems]);

  const toggleExpanded = (id: string) => {
    if (isDesktop) return; // no-op on desktop
    setExpandedItems((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleItemClick = (item: IMegaMenuNavigationItemColumn) => {
    // If the item has a link, open it; otherwise toggle ONLY on mobile
    const url = getActivePathFromLink(item.link);
    const newTab = item.link?.type === 'external' || item.link?.isOpenInNewTab ? '_blank' : '_self';

    if (url) {
      window.open(url, newTab);
    } else if (!isDesktop) {
      toggleExpanded(item._key);
    }
  };

  const handleClickAdBanner = () => {
    if (!adBanner) return;
    const url = adBanner.link?.href?.trim();
    const newTab = shouldOpenInNewTab(adBanner.link) ? '_blank' : '_self';

    if (url) {
      window.open(url, newTab);
    }
  };

  return (
    <MegaMenuContainer
      className={cn('pb-xl pt-xl p-4xl overflow-y-auto', adBanner ? 'min-h-132.5' : 'min-h-87.5')}
      isClosing={isClosing}
    >
      <div className="flex justify-between w-full max-w-300 mx-auto lg:gap-0 px-0">
        <div
          className={cn(
            'flex flex-wrap gap-xl gap-y-30',
            adBanner ? 'w-2/3 max-w-205' : 'w-full',
            adBanner && 'border-b border-b-secondary-100 sm:border-b-0 sm:border-r sm:border-r-secondary-100 lg:pr-2xl',
          )}
        >
          {visibleMenuItems.map((item) => (
            <MenuItem
              key={item._key}
              item={item}
              isExpanded={expandedItems.has(item._key)}
              onItemClick={handleItemClick}
              showChevron={!isDesktop}
            />
          ))}
        </div>

        {adBanner ? (
          <div className="pt-lg sm:pt-0 sm:pl-0 sm:pr-0 lg:pl-0 lg:pr-0 flex flex-col gap-xs">
            {adBannerImage ? (
              <Image
                src={sanityImageSrc(adBannerImage, {
                  width: 300,
                  height: 300,
                })}
                alt={normalizeAltText(adBannerAlt) || 'ad banner'}
                width={300}
                height={300}
                className="object-cover cursor-pointer mt-13.25"
                onClick={handleClickAdBanner}
              />
            ) : null}

            {adBanner?.label ? (
              <div>
                <Typography
                  variant="link"
                  className="text-sm no-underline font-normal text-secondary-400 cursor-pointer"
                  onClick={handleClickAdBanner}
                >
                  {adBanner.label}
                </Typography>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </MegaMenuContainer>
  );
}
